import { CustomFieldType } from '@ee/common/enums';
import { CompositeType, CustomField, LayoutItem, LayoutRow, PlacementLockReason, SizeHint } from '@ee/common/models';

export { SIZE_HINT_LABEL, SIZE_HINT_SPAN, sizeHintForColumns } from '@ee/common/models';
export type { SizeHint } from '@ee/common/models';

/**
 * The builder's view of the world. Three layers, kept apart on purpose:
 *
 *  - a **definition** is org-wide and says what a field *is*;
 *  - a **placement** (`LayoutItem`) is per case type + surface and says where it sits and how wide;
 *  - **rules** live on the case template, not on a form.
 *
 * Conflating the first two is what let the same field acquire four names and three types across case
 * types, so nothing here lets a layout edit reach a definition.
 */

/** One row of the org-wide registry. */
export interface FieldDefinitionView {
  key: string;
  label: string;
  type: CustomFieldType;
  options?: string[];
  hint?: string;
  size_hint: SizeHint;
  /** Where the definition came from: a product built-in or something the firm created. */
  origin: 'BUILT_IN' | 'CUSTOM';
  /** Calculated and offset-date fields are display-only everywhere; no editor is ever created for them. */
  smart?: boolean;
  monetary?: boolean;
  /** Composites are placed as a unit; their parts are never individually placeable. */
  composite?: CompositeType;
  /** Blocked from deletion while anything references it. */
  references?: FieldReference[];
  /**
   * The product's own choice list, for a question whose choices a firm may narrow.
   *
   * Distinct from [options], which is the *effective* list — the firm's where it set one, the
   * product's otherwise. Knowing both is what lets the panel say "this is the standard list" and offer
   * a reset that keeps following it.
   */
  standard_options?: string[];
  /** The `IntakeOption` key a narrowed choice list is stored under. Absent when there is nothing to narrow. */
  option_key?: string;
  /**
   * The full field this view describes, where one exists. `ee-field` draws from it — masks, defaults,
   * option labels, true/false labels, formats — so nothing a firm configured is lost between the
   * registry and the form.
   */
  field?: CustomField;
  /** A party (tenants): each row is these fields. Present only on a subject collection. */
  subject_fields?: FieldDefinitionView[];
  /** "Tenant" — what one row of a subject collection is called. */
  subject_label_singular?: string;
  /** Adds into its section's Total (a DECIMAL, or a rent history's monthly amounts). */
  include_in_total?: boolean;
  /** Subtracts from its section's Total (partial payments). */
  subtract_from_total?: boolean;
}

/**
 * A choice list a firm may edit, and where it is stored.
 *
 * Three questions have one — the eviction reasons, the property types and the address block's unit
 * types — and all three are stored the same way: an `options` array on an `IntakeOption`, empty meaning
 * "offer the product's list". One descriptor rather than three controls, because three copies of a
 * list editor is how three surfaces drift.
 */
export interface ChoiceListSpec {
  /**
   * Whether a firm may invent choices, or only pick from the product's.
   *
   * `FREE` for a list whose values are just strings the form stores — the unit types. `SUBSET` where a
   * value means something downstream and an invented one would break it: an eviction reason keys
   * `EVICTION_REASONS_CLAIMS_MAP`, and the property types are three hard-coded radio buttons in the
   * wizard. Those can be shown or hidden and nothing else.
   */
  mode: 'FREE' | 'SUBSET';
  /** Where the narrowed list is stored on the form. */
  optionKey: string;
  /** "Unit types offered". */
  label: string;
  /** What the list is, in the client's terms. */
  help: string;
  /** The product's list, offered when the firm has narrowed nothing. */
  standard: string[];
  /** What the firm has chosen. Empty means not narrowed. */
  current: string[];
}

export interface FieldReference {
  kind: 'RULE' | 'MERGE_FIELD' | 'IMPORT_MAPPING' | 'SIDEBAR' | 'PLACEMENT';
  label: string;
  /** Where to go to remove the reference. */
  link?: string;
}

/** A section of a layout — intake page, workflow step, or sidebar panel. */
export interface LayoutSectionView {
  key: string;
  title: string;
  description?: string;
  rows: LayoutRow[];
  /** Firm-only sections never render on the public form at all. */
  firm_only?: boolean;
  /** Product-owned sections (Documents, Billing, Activity…) reorder and hide but cannot be edited. */
  content_owned_by_product?: boolean;
  /**
   * The *fields* are the product's, but headings and help text are not.
   *
   * Distinct from [content_owned_by_product], which fixes everything. The public form's first page is
   * this case: which questions it asks is not negotiable — they create the client's account — but a
   * firm can still introduce the page and explain itself around them.
   */
  fields_owned_by_product?: boolean;
  /**
   * Every placement in this section is full width and cannot be resized.
   *
   * Documents: an upload row is a file picker and a filename, so a narrower one only truncates the name.
   */
  full_width_only?: boolean;
  /**
   * Suppress the storage keys under this section's labels.
   *
   * Keys are shown everywhere a firm placed the field, because they are what identifies it on every
   * other surface. They are noise on a section a firm did not compose: `first_name`, `LEASE_AGREEMENT`
   * and `note` are ours, they cannot be changed from here, and printing them only crowds the label.
   */
  hide_field_keys?: boolean;
  /** Stated on the section rather than silently disabling its controls. */
  locked_reason?: string;
  /**
   * No field moves in or out of this section from another. A declared section that is not merged keeps
   * only its own questions on read, so a field moved in would vanish and one moved out would come back.
   */
  closed_to_moves?: boolean;
}

export interface LayoutPageView {
  id: string;
  name: string;
  public_only?: boolean;
  fixed?: boolean;
  post_submit?: boolean;
  /**
   * What a page with no sections contains, said out loud.
   *
   * Review & file is generated rather than authored, so the canvas has nothing to draw for it. Left to
   * the empty state it read "Nothing on this page yet." — which describes a page waiting to be filled,
   * not one whose content is produced for you.
   */
  note?: string;
  sections: LayoutSectionView[];
}

/**
 * Role-scoped chrome. One editor, not two: storage keys, layout version and migration diagnostics sit
 * behind *Advanced* for firm users and are shown by default for staff; guardrails **block** for firm
 * users and merely warn for staff.
 */
export interface LayoutBuilderCapabilities {
  /** True for EasyEviction staff. */
  staff: boolean;
  /** Storage keys and layout diagnostics visible without the Advanced reveal. */
  showAdvanced: boolean;
  /** Resize restricted to spans v1 can express. Default-on for firm users during the migration window. */
  legacySnap: boolean;
  /** Guardrails block rather than warn. */
  blockOnViolation: boolean;
  canEditLayout: boolean;
  canCreateFields: boolean;
  /** Pages are only authorable on the intake surface. */
  pagesAuthorable: boolean;
  /** The sidebar panel has one column, so the span control is not rendered at all. */
  spansAuthorable: boolean;
  /** Advisory once a form passes this many pages. */
  pageAdvisoryLimit: number;
}

export const DEFAULT_CAPABILITIES: LayoutBuilderCapabilities = {
  staff: false,
  showAdvanced: false,
  legacySnap: true,
  blockOnViolation: true,
  canEditLayout: true,
  canCreateFields: true,
  pagesAuthorable: true,
  spansAuthorable: true,
  pageAdvisoryLimit: 6
};

/** Which placements can never be removed, and why. Stated in place, never as a disabled control. */
export const LOCKED_PLACEMENTS: Record<string, PlacementLockReason> = {
  plaintiff_name: 'ALWAYS_REQUIRED',
  tenants: 'ALWAYS_REQUIRED',
  contact_email: 'CLIENT_CREATION',
  contact_phone: 'CLIENT_CREATION',
  company_name: 'CLIENT_CREATION',
  first_name: 'CLIENT_CREATION',
  last_name: 'CLIENT_CREATION'
};

/**
 * Fields that are never questions on the intake form.
 *
 * `tags` is a case-management label staff apply to a case after it exists — it is not something a client
 * can answer, and the wizard has nothing sensible to render for it. It was reaching the canvas because
 * `toDefinitions` offered every field the org knows about and only the v1 projection filtered anything.
 *
 * Distinct from `hide_from_intake`, which is a firm's choice about one of its own fields; this is a
 * property of what the field *is*, so it is not offered in the picker either.
 */
export const NEVER_ON_INTAKE = new Set<string>(['tags']);

/**
 * Fields of a tenant rather than of the case.
 *
 * The wizard asks these once per tenant, inside a per-tenant group — the storage path is
 * `tenants[i].email` — so the prefix is what tells a party field from a case field, and it is what
 * keeps Add field from offering one where it cannot mean anything.
 */
export const TENANT_FIELD_PREFIX = 'tenants.';

export function isTenantField(fieldKey: string | null | undefined): boolean {
  return !!fieldKey && fieldKey.startsWith(TENANT_FIELD_PREFIX);
}

export function isIntakePlaceable(fieldKey: string | null | undefined): boolean {
  return !!fieldKey && !NEVER_ON_INTAKE.has(fieldKey);
}

export function lockReasonFor(fieldKey: string | null | undefined): PlacementLockReason | null {
  return fieldKey ? (LOCKED_PLACEMENTS[fieldKey] ?? null) : null;
}

/** The selection is shared across surfaces, so jumping to a placement and back keeps context. */
export interface LayoutSelection {
  pageId?: string;
  sectionKey: string;
  rowIndex: number;
  itemIndex: number;
}

export function itemAt(sections: LayoutSectionView[], selection: LayoutSelection | null): LayoutItem | null {
  if (!selection) return null;
  const section = sections.find((s) => s.key === selection.sectionKey);
  return section?.rows[selection.rowIndex]?.items[selection.itemIndex] ?? null;
}

/** `Property · 6 fields · 4 rows` — what a section header states without being opened. */
export function sectionSummary(section: LayoutSectionView): string {
  const fields = section.rows.reduce(
    (count, row) => count + row.items.filter((item) => item.kind === 'FIELD' || item.kind === 'COMPOSITE').length,
    0
  );
  const rows = section.rows.length;
  return `${fields} field${fields === 1 ? '' : 's'} · ${rows} row${rows === 1 ? '' : 's'}`;
}

/** What a palette entry carries when dragged onto the canvas. */
export type PaletteDragData =
  | { kind: 'field'; definition: FieldDefinitionView }
  | { kind: 'block'; blockKind: 'HEADING' | 'RICH_TEXT' | 'DIVIDER' };

/** The palette list's `cdkDropListData`, so a drop handler can tell a palette drag from a row move. */
export const PALETTE_LIST_DATA = -2;

/** A heading's word, for comparing two headings: case, spacing and a trailing plural are ignored. */
function headingWord(text: string | null | undefined): string {
  return (text ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/(ies|s)$/, (m) => (m === 'ies' ? 'y' : ''));
}

/** Whether two headings say the same thing ("Tenant" and "Tenants" do), so the inner one can stay quiet. */
export function sameHeading(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = headingWord(a);
  return !!left && left === headingWord(b);
}

/** The singular of a collection's label, for its rows when it has no singular label: "Tenants" → "Tenant". */
export function singularOf(label: string | null | undefined): string {
  const text = (label ?? '').trim();
  if (/ies$/i.test(text)) return text.slice(0, -3) + 'y';
  if (/[^s]s$/i.test(text)) return text.slice(0, -1);
  return text || 'Entry';
}
